import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { db } from "../../firebase";
import { collection, query, where, getDocs } from "firebase/firestore";
import {
  fetchSparePartRequests,
  resolveToWarranty,
  resolveToBuy,
  resolveToAlreadyWithUs,
  resolveToRepair,
  completeWarrantyReplacement,
  completeBuyReceipt,
  completeRepairReturn,
  fetchAvailablePartInstances,
  fetchInstalledPartInstances,
  createManualPartInstance,
  registerInstalledPartInstance,
} from "../../services/sparePartService";

const STATUS_CFG = {
  requested:        { label: "Requested",        bg: "#E8F4FD", color: "#1565C0" },
  under_review:      { label: "Under Review",     bg: "#FFF8E1", color: "#E65100" },
  sent_to_oem:       { label: "Sent to OEM",      bg: "#F3E5F5", color: "#6A1B9A" },
  po_created:        { label: "PO Created",       bg: "#E0F2FE", color: "#0369A1" },
  at_repair_center:  { label: "At Repair Center", bg: "#FEF3C7", color: "#92400E" },
  closed:            { label: "Closed",           bg: "#E8F5E9", color: "#1B5E20" },
};

const inp = { border: "1px solid #E5E7EB", borderRadius: 8, padding: "8px 10px", width: "100%", fontSize: 13, outline: "none", boxSizing: "border-box", color: "#111827", marginTop: 8 };
const card = { background: "#fff", border: "1px solid #F1F1F1", borderRadius: 14, padding: "16px 18px", marginBottom: 12 };
const label = { fontSize: 11, color: "#9CA3AF", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10, display: "block" };

export default function SparePartRequests() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const preselectServiceId = location.state?.preselect_service_id || "";

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");

  // Which request currently has a triage/complete panel open, and which action type
  const [activePanel, setActivePanel] = useState(null); // { requestId, action }
  const [submitting, setSubmitting] = useState(false);

  // Shared form fields across all panel types (only relevant ones used per action)
  const [form, setForm] = useState({
    oem_return_id: "",
    po_id: "",
    partInstanceId: "",
    machine_id: "",
    received_serial_no: "",
    reinstall: false,
    legacy_part_no: "",
    legacy_brand: "",
    legacy_serial_no: "",
  });
  const [registeringLegacy, setRegisteringLegacy] = useState(false);

  const [availableInstances, setAvailableInstances] = useState([]);
  const [installedInstances, setInstalledInstances] = useState([]);
  const [siteMachines, setSiteMachines] = useState([]);
  const [oemReturns, setOemReturns] = useState([]);
const [directPOs, setDirectPOs] = useState([]);

  // Onboarding / backfill — register existing physical stock that never
  // went through a spare_part_request (e.g. opening stock when this system
  // first goes live for a company).
  const [showAddStock, setShowAddStock] = useState(false);
  const [addingStock, setAddingStock] = useState(false);
  const [stockForm, setStockForm] = useState({ part_name: "", part_no: "", brand: "", serial_no: "" });
  const [inStoreInstances, setInStoreInstances] = useState([]);

  useEffect(() => {
    loadRequests();
    loadInStoreInstances();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadRequests = async () => {
    if (!user.company_id) return;
    setLoading(true);
    try {
      const results = await fetchSparePartRequests(user.company_id);
      setRequests(results);
    } catch (err) {
      console.error("Failed to load spare part requests:", err.message);
    } finally {
      setLoading(false);
    }
  };

  // Shows every serialized part currently sitting "in_store" — gives
  // visual confirmation after "+ Add Existing Stock", and lets Store see
  // what's on the shelf without having to wait for a matching request.
  const loadInStoreInstances = async () => {
    if (!user.company_id) return;
    try {
      const snap = await getDocs(query(
        collection(db, "part_instances"),
        where("company_id", "==", user.company_id),
        where("current_status", "==", "in_store")
      ));
      const results = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      results.sort((a, b) => (b.created_at?.seconds || 0) - (a.created_at?.seconds || 0));
      setInStoreInstances(results);
    } catch (err) {
      console.warn("In-store instances fetch failed:", err.message);
    }
  };

  const submitAddStock = async () => {
    if (addingStock) return;
    if (!stockForm.part_name.trim()) { alert("❌ Part name required"); return; }
    if (!stockForm.serial_no.trim()) { alert("❌ Serial No. required — this tool is only for serialized parts"); return; }
    setAddingStock(true);
    try {
      await createManualPartInstance({
        company_id: user.company_id,
        part_name: stockForm.part_name.trim(),
        part_no: stockForm.part_no.trim(),
        brand: stockForm.brand.trim(),
        serial_no: stockForm.serial_no.trim(),
        created_by: user.uid || user.id,
        created_by_name: user.name,
      });
      setStockForm({ part_name: "", part_no: "", brand: "", serial_no: "" });
      setShowAddStock(false);
      alert("✅ Added to stock — it will now appear under \"Already With Us\" for matching requests.");
      loadInStoreInstances();
    } catch (err) {
      alert("Error: " + err.message);
    } finally {
      setAddingStock(false);
    }
  };

  const fetchMachinesForSite = async (site_id) => {
    if (!site_id) { setSiteMachines([]); return; }
    try {
      const snap = await getDocs(query(
        collection(db, "site_machines"),
        where("company_id", "==", user.company_id),
        where("site_id", "==", site_id)
      ));
      setSiteMachines(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.warn("Machine fetch failed:", err.message);
    }
  };

  const fetchOemReturnsList = async () => {
    try {
      const snap = await getDocs(query(
        collection(db, "oem_returns"),
        where("company_id", "==", user.company_id)
      ));
      const results = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      results.sort((a, b) => (b.created_at?.seconds || 0) - (a.created_at?.seconds || 0));
      setOemReturns(results);
    } catch (err) {
      console.warn("OEM Returns fetch failed:", err.message);
    }
  };

  const fetchDirectPOsList = async () => {
    try {
      const snap = await getDocs(query(
        collection(db, "purchase_orders"),
        where("company_id", "==", user.company_id),
        where("source", "==", "direct")
      ));
      const results = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(po => (po.po_status || po.status || "draft") !== "cancelled");
      results.sort((a, b) => (b.created_at?.seconds || 0) - (a.created_at?.seconds || 0));
      setDirectPOs(results);
    } catch (err) {
      console.warn("Direct PO fetch failed:", err.message);
    }
  };

  const openPanel = async (request, action) => {
    setActivePanel({ requestId: request.id, action });
    setForm({
      oem_return_id: "", po_id: "", partInstanceId: "",
      machine_id: request.machine_id || "", received_serial_no: "", reinstall: false,
      legacy_part_no: "", legacy_brand: "", legacy_serial_no: "",
    });
    setAvailableInstances([]);
    setInstalledInstances([]);
    setOemReturns([]);
    setDirectPOs([]);

    await fetchMachinesForSite(request.site_id);

    if (action === "warranty") {
      await fetchOemReturnsList();
    }

    if (action === "buy") {
      await fetchDirectPOsList();
    }

    if (action === "already_with_us") {
      const instances = await fetchAvailablePartInstances({
        company_id: user.company_id, part_name: request.part_name, part_no: request.part_no,
      });
      setAvailableInstances(instances);
    }

    if (action === "repair" && request.machine_id) {
      const instances = await fetchInstalledPartInstances({
        company_id: user.company_id, machine_id: request.machine_id, part_name: request.part_name,
      });
      setInstalledInstances(instances);
    }

    if (action === "complete_repair") {
      const instances = await fetchInstalledPartInstances({
        company_id: user.company_id, machine_id: "", part_name: "",
      }).catch(() => []);
      // Not used directly here — repair completion uses the request's own
      // linked_part_instance_id, no picker needed. Kept for symmetry.
      void instances;
    }
  };

  const closePanel = () => {
    setActivePanel(null);
    setForm({ oem_return_id: "", po_id: "", partInstanceId: "", machine_id: "", received_serial_no: "", reinstall: false, legacy_part_no: "", legacy_brand: "", legacy_serial_no: "" });
    setDirectPOs([]);
  };

  const doResolveWarranty = async (request) => {
    if (!form.oem_return_id.trim()) { alert("❌ OEM Return ID required"); return; }
    setSubmitting(true);
    try {
      await resolveToWarranty({
        requestId: request.id,
        company_id: user.company_id,
        oem_return_id: form.oem_return_id.trim(),
        resolved_by: user.uid || user.id,
        resolved_by_name: user.name,
      });
      alert("✅ Routed to Warranty (OEM Returns)");
      closePanel(); loadRequests();
    } catch (err) { alert("Error: " + err.message); }
    finally { setSubmitting(false); }
  };

  const doResolveBuy = async (request) => {
    if (!form.po_id.trim()) { alert("❌ PO ID required"); return; }
    setSubmitting(true);
    try {
      await resolveToBuy({
        requestId: request.id,
        company_id: user.company_id,
        po_id: form.po_id.trim(),
        resolved_by: user.uid || user.id,
        resolved_by_name: user.name,
      });
      alert("✅ Routed to Purchase Order");
      closePanel(); loadRequests();
    } catch (err) { alert("Error: " + err.message); }
    finally { setSubmitting(false); }
  };

  const doResolveAlreadyWithUs = async (request) => {
    if (!form.partInstanceId) { alert("❌ Select a part instance"); return; }
    if (!form.machine_id) { alert("❌ Select a machine"); return; }
    setSubmitting(true);
    try {
      await resolveToAlreadyWithUs({
        requestId: request.id,
        company_id: user.company_id,
        partInstanceId: form.partInstanceId,
        machine_id: form.machine_id,
        resolved_by: user.uid || user.id,
        resolved_by_name: user.name,
      });
      alert("✅ Installed from existing stock");
      closePanel(); loadRequests(); loadInStoreInstances();
    } catch (err) { alert("Error: " + err.message); }
    finally { setSubmitting(false); }
  };

  const doResolveRepair = async (request) => {
    if (!form.partInstanceId) { alert("❌ Select the part instance being sent for repair"); return; }
    setSubmitting(true);
    try {
      await resolveToRepair({
        requestId: request.id,
        company_id: user.company_id,
        oldPartInstanceId: form.partInstanceId,
        resolved_by: user.uid || user.id,
        resolved_by_name: user.name,
      });
      alert("✅ Sent for repair");
      closePanel(); loadRequests();
    } catch (err) { alert("Error: " + err.message); }
    finally { setSubmitting(false); }
  };

  const doRegisterLegacyAndRepair = async (request) => {
    if (!form.legacy_serial_no.trim()) { alert("❌ Serial No. required"); return; }
    if (form.legacy_serial_no.trim().toLowerCase() === (request.part_name || "").trim().toLowerCase()) {
      alert(`❌ "${form.legacy_serial_no.trim()}" looks like the part name, not a serial number. Please check the physical part and enter its actual serial number (usually printed as S/N, SR NO, or Serial on a label/plate on the part).`);
      return;
    }
    if (!request.machine_id) { alert("❌ This request has no machine linked — cannot register an installed part"); return; }
    setRegisteringLegacy(true);
    try {
      const newInstanceId = await registerInstalledPartInstance({
        company_id: user.company_id,
        machine_id: request.machine_id,
        part_name: request.part_name,
        part_no: form.legacy_part_no.trim() || request.part_no || "",
        brand: form.legacy_brand.trim() || request.brand || "",
        serial_no: form.legacy_serial_no.trim(),
        created_by: user.uid || user.id,
        created_by_name: user.name,
      });
      await resolveToRepair({
        requestId: request.id,
        company_id: user.company_id,
        oldPartInstanceId: newInstanceId,
        resolved_by: user.uid || user.id,
        resolved_by_name: user.name,
      });
      alert("✅ Registered and sent for repair");
      closePanel(); loadRequests();
    } catch (err) { alert("Error: " + err.message); }
    finally { setRegisteringLegacy(false); }
  };

  const doCompleteWarranty = async (request) => {
    // This system tracks serialized parts only — received serial no. is
    // always required, regardless of the legacy is_serialized field value.
    if (!form.received_serial_no.trim()) {
      alert("❌ Received serial no. required"); return;
    }
    setSubmitting(true);
    try {
      await completeWarrantyReplacement({
        requestId: request.id,
        company_id: user.company_id,
        received_serial_no: form.received_serial_no.trim(),
        machine_id: form.machine_id,
        done_by: user.uid || user.id,
        done_by_name: user.name,
      });
      alert("✅ Warranty replacement completed");
      closePanel(); loadRequests(); loadInStoreInstances();
    } catch (err) { alert("Error: " + err.message); }
    finally { setSubmitting(false); }
  };

  const doCompleteBuy = async (request) => {
    // This system tracks serialized parts only — received serial no. is
    // always required, regardless of the legacy is_serialized field value.
    if (!form.received_serial_no.trim()) {
      alert("❌ Received serial no. required"); return;
    }
    setSubmitting(true);
    try {
      await completeBuyReceipt({
        requestId: request.id,
        company_id: user.company_id,
        received_serial_no: form.received_serial_no.trim(),
        machine_id: form.machine_id,
        done_by: user.uid || user.id,
        done_by_name: user.name,
      });
      alert("✅ Buy receipt completed");
      closePanel(); loadRequests(); loadInStoreInstances();
    } catch (err) { alert("Error: " + err.message); }
    finally { setSubmitting(false); }
  };

  const doCompleteRepair = async (request) => {
    if (form.reinstall && !form.machine_id) { alert("❌ Select machine to reinstall into"); return; }
    setSubmitting(true);
    try {
      await completeRepairReturn({
        requestId: request.id,
        company_id: user.company_id,
        partInstanceId: request.linked_part_instance_id,
        reinstall: form.reinstall,
        machine_id: form.machine_id,
        done_by: user.uid || user.id,
        done_by_name: user.name,
      });
      alert("✅ Repair return completed");
      closePanel(); loadRequests(); loadInStoreInstances();
    } catch (err) { alert("Error: " + err.message); }
    finally { setSubmitting(false); }
  };

  const filteredRequests = requests.filter((r) => {
    if (preselectServiceId && r.service_id !== preselectServiceId) return false;
    if (statusFilter === "all") return true;
    if (statusFilter === "pending") return r.resolution_type === "pending";
    if (statusFilter === "in_progress") return ["sent_to_oem", "po_created", "at_repair_center"].includes(r.status);
    if (statusFilter === "closed") return r.status === "closed";
    return true;
  });

  if (loading) {
    return <div style={{ padding: 30, color: "#9CA3AF", fontSize: 14 }}>Loading spare part requests...</div>;
  }

  return (
    <div style={{ padding: "20px", background: "#F8F9FB", minHeight: "100vh", fontFamily: "system-ui,sans-serif" }}>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: "#111827", margin: 0 }}>Spare Part Requests</h2>
          {preselectServiceId && (
            <button onClick={() => navigate("/spare-part-requests")} style={{ background: "none", border: "none", color: "#3B82F6", fontSize: 12, cursor: "pointer", padding: 0, marginTop: 4 }}>
              ← Showing only this job's requests — view all
            </button>
          )}
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <button onClick={() => setShowAddStock(!showAddStock)}
            style={{ background: "#0F766E", color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
            {showAddStock ? "Cancel" : "+ Add Existing Stock"}
          </button>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ ...inp, marginTop: 0, width: 200 }}>
            <option value="all">All Requests</option>
            <option value="pending">Pending Triage</option>
            <option value="in_progress">In Progress</option>
            <option value="closed">Closed</option>
          </select>
        </div>
      </div>

      {showAddStock && (
        <div style={{ ...card, background: "#F0FDFA", border: "1px solid #99F6E4" }}>
          <span style={{ ...label, color: "#0F766E" }}>Register Existing Physical Stock</span>
          <p style={{ fontSize: 11, color: "#6B7280", marginBottom: 10 }}>
            Use this only for a part you already physically have (opening stock, or an untracked part found in store) —
            not for new requests. New requests should always go through the normal Warranty/Buy flow.
          </p>
          <input placeholder="Part Name *" value={stockForm.part_name}
            onChange={e => setStockForm(p => ({ ...p, part_name: e.target.value }))} style={{ ...inp, marginTop: 0 }} />
          <input placeholder="Part No." value={stockForm.part_no}
            onChange={e => setStockForm(p => ({ ...p, part_no: e.target.value }))} style={inp} />
          <input placeholder="Brand" value={stockForm.brand}
            onChange={e => setStockForm(p => ({ ...p, brand: e.target.value }))} style={inp} />
          <input placeholder="Serial No. *" value={stockForm.serial_no}
            onChange={e => setStockForm(p => ({ ...p, serial_no: e.target.value }))} style={inp} />
          <button onClick={submitAddStock} disabled={addingStock}
            style={{ marginTop: 10, width: "100%", background: "#0F766E", color: "#fff", border: "none", borderRadius: 8, padding: "10px 0", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            {addingStock ? "Adding..." : "✅ Add to Stock"}
          </button>
        </div>
      )}

      {inStoreInstances.length > 0 && (
        <div style={{ ...card, background: "#F8FAFC" }}>
          <span style={label}>📦 In-Store Parts ({inStoreInstances.length})</span>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {inStoreInstances.map(inst => (
              <div key={inst.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "#fff", border: "1px solid #E5E7EB", borderRadius: 8, padding: "8px 10px", fontSize: 12 }}>
                <div>
                  <b style={{ color: "#111827" }}>{inst.part_name}</b>
                  {inst.brand && <span style={{ color: "#6B7280" }}> · {inst.brand}</span>}
                  {inst.part_no && <span style={{ color: "#6B7280" }}> · {inst.part_no}</span>}
                  <div style={{ color: "#6B7280" }}>S/N: {inst.serial_no}</div>
                </div>
                <span style={{ color: "#0F766E", fontWeight: 700, fontSize: 11 }}>{inst.part_instance_no}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {filteredRequests.length === 0 && (
        <div style={{ ...card, textAlign: "center", color: "#9CA3AF" }}>No spare part requests found.</div>
      )}

      {filteredRequests.map((request) => {
        const st = STATUS_CFG[request.status] || STATUS_CFG.requested;
        const isPanelOpen = activePanel?.requestId === request.id;

        return (
          <div key={request.id} style={card}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>
                  {request.part_name} × {request.quantity}
                </div>
                <div style={{ fontSize: 12, color: "#6B7280", marginTop: 2 }}>
                  {request.request_no} · {request.client_name || ""} {request.site_id ? `· Site` : ""}
                </div>
              </div>
              <span style={{ background: st.bg, color: st.color, fontSize: 11, fontWeight: 700, padding: "4px 12px", borderRadius: 20 }}>
                {st.label}
              </span>
            </div>

            <div style={{ display: "flex", gap: 16, marginTop: 10, flexWrap: "wrap", fontSize: 12, color: "#6B7280" }}>
              {request.part_no && <span>Part No: <b style={{ color: "#111827" }}>{request.part_no}</b></span>}
              {request.serial_no && <span>S/N: <b style={{ color: "#111827" }}>{request.serial_no}</b></span>}
              {request.brand && <span>Brand: <b style={{ color: "#111827" }}>{request.brand}</b></span>}
              <span>Requested by: <b style={{ color: "#111827" }}>{request.requested_by_name}</b></span>
              
            </div>

            {request.notes && (
              <div style={{ fontSize: 12, color: "#6B7280", marginTop: 8, fontStyle: "italic" }}>"{request.notes}"</div>
            )}

            {/* ── TRIAGE ACTIONS (only when pending) ── */}
            {request.resolution_type === "pending" && (
              <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                <button onClick={() => openPanel(request, "warranty")}
                  style={{ background: "#7C3AED", color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                  🛡 Warranty
                </button>
                <button onClick={() => openPanel(request, "buy")}
                  style={{ background: "#0369A1", color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                  🛒 Buy
                </button>
                {/* This system tracks ONLY serialized parts — every request
                    is treated as serialized by definition. is_serialized
                    !== false (not === true) so legacy pre-this-fix docs
                    (missing the field, or explicitly false from the old
                    optional checkbox) still show these options — only a
                    doc that was EXPLICITLY marked false stays hidden,
                    which should no longer happen for any new request. */}
                <button onClick={() => openPanel(request, "already_with_us")}
                  style={{ background: "#16A34A", color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                  📦 Already With Us
                </button>
                {request.machine_id && (
                  <button onClick={() => openPanel(request, "repair")}
                    style={{ background: "#EA580C", color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                    🔧 Send for Repair
                  </button>
                )}
              </div>
            )}

            {/* ── COMPLETION ACTIONS (in-progress branches) ── */}
            {request.status === "sent_to_oem" && (
              <button onClick={() => openPanel(request, "complete_warranty")}
                style={{ marginTop: 12, background: "#7C3AED", color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                ✅ Mark Replacement Received
              </button>
            )}
            {request.status === "po_created" && (
              <button onClick={() => openPanel(request, "complete_buy")}
                style={{ marginTop: 12, background: "#0369A1", color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                ✅ Mark GRN Received
              </button>
            )}
            {request.status === "at_repair_center" && (
              <button onClick={() => openPanel(request, "complete_repair")}
                style={{ marginTop: 12, background: "#EA580C", color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                ✅ Mark Repaired Part Returned
              </button>
            )}

            {/* ── PANELS ── */}
            {isPanelOpen && activePanel.action === "warranty" && (
              <div style={{ marginTop: 12, background: "#F5F3FF", border: "1px solid #DDD6FE", borderRadius: 10, padding: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#6A1B9A", marginBottom: 6 }}>Link to OEM Return</div>
                {oemReturns.length === 0 ? (
                  <p style={{ fontSize: 12, color: "#6B7280" }}>No OEM Return records found for this company yet.</p>
                ) : (
                  <select value={form.oem_return_id} onChange={e => setForm(p => ({ ...p, oem_return_id: e.target.value }))} style={{ ...inp, marginTop: 0 }}>
                    <option value="">Select OEM Return</option>
                    {oemReturns.map(oem => (
                      <option key={oem.id} value={oem.id}>
                        {oem.oem_return_no} — {oem.oem_name || "OEM not specified"} ({oem.status})
                      </option>
                    ))}
                  </select>
                )}
                <button
                  onClick={() => navigate("/oem-returns")}
                  style={{ background: "none", border: "none", color: "#6A1B9A", fontSize: 11, fontWeight: 700, cursor: "pointer", padding: 0, marginTop: 6, display: "block" }}>
                  Don't see it? + Create a new OEM Return →
                </button>
                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                  <button onClick={closePanel} style={{ flex: 1, background: "#fff", border: "1px solid #E5E7EB", borderRadius: 8, padding: "8px 0", fontSize: 12, cursor: "pointer" }}>Cancel</button>
                  <button onClick={() => doResolveWarranty(request)} disabled={submitting || !form.oem_return_id} style={{ flex: 1, background: "#7C3AED", color: "#fff", border: "none", borderRadius: 8, padding: "8px 0", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                    {submitting ? "..." : "Confirm"}
                  </button>
                </div>
              </div>
            )}

            {isPanelOpen && activePanel.action === "buy" && (
              <div style={{ marginTop: 12, background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 10, padding: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#0369A1", marginBottom: 6 }}>Link to Purchase Order</div>
                {directPOs.length === 0 ? (
                  <p style={{ fontSize: 12, color: "#6B7280" }}>No Direct Site Purchase PO found for this company yet.</p>
                ) : (
                  <select value={form.po_id} onChange={e => setForm(p => ({ ...p, po_id: e.target.value }))} style={{ ...inp, marginTop: 0 }}>
                    <option value="">Select Purchase Order</option>
                    {directPOs.map(po => (
                      <option key={po.id} value={po.id}>
                        {po.po_number || "No PO Number"} — {po.vendor_name || "No Vendor"} ({po.po_status || po.status || "draft"})
                      </option>
                    ))}
                  </select>
                )}
                <button
                  onClick={() => navigate("/purchase-orders/create")}
                  style={{ background: "none", border: "none", color: "#0369A1", fontSize: 11, fontWeight: 700, cursor: "pointer", padding: 0, marginTop: 6, display: "block" }}>
                  Don't see it? + Create a Direct Site Purchase PO →
                </button>
                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                  <button onClick={closePanel} style={{ flex: 1, background: "#fff", border: "1px solid #E5E7EB", borderRadius: 8, padding: "8px 0", fontSize: 12, cursor: "pointer" }}>Cancel</button>
                  <button onClick={() => doResolveBuy(request)} disabled={submitting || !form.po_id} style={{ flex: 1, background: "#0369A1", color: "#fff", border: "none", borderRadius: 8, padding: "8px 0", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                    {submitting ? "..." : "Confirm"}
                  </button>
                </div>
              </div>
            )}

            {isPanelOpen && activePanel.action === "already_with_us" && (
              <div style={{ marginTop: 12, background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 10, padding: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#166534", marginBottom: 6 }}>Select Existing Part Instance</div>
                {availableInstances.length === 0 ? (
                  <p style={{ fontSize: 12, color: "#6B7280" }}>No matching in-store part instances found.</p>
                ) : (
                  <select value={form.partInstanceId} onChange={e => setForm(p => ({ ...p, partInstanceId: e.target.value }))} style={{ ...inp, marginTop: 0 }}>
                    <option value="">Select instance</option>
                    {availableInstances.map(inst => (
                      <option key={inst.id} value={inst.id}>{inst.part_instance_no} · S/N {inst.serial_no}</option>
                    ))}
                  </select>
                )}
                <select value={form.machine_id} onChange={e => setForm(p => ({ ...p, machine_id: e.target.value }))} style={inp}>
                  <option value="">Select machine to install into *</option>
                  {siteMachines.map(m => (
                    <option key={m.id} value={m.id}>{m.machine_name || "Unnamed"} {m.model_no ? `· ${m.model_no}` : ""}</option>
                  ))}
                </select>
                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                  <button onClick={closePanel} style={{ flex: 1, background: "#fff", border: "1px solid #E5E7EB", borderRadius: 8, padding: "8px 0", fontSize: 12, cursor: "pointer" }}>Cancel</button>
                  <button onClick={() => doResolveAlreadyWithUs(request)} disabled={submitting || availableInstances.length === 0} style={{ flex: 1, background: "#16A34A", color: "#fff", border: "none", borderRadius: 8, padding: "8px 0", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                    {submitting ? "..." : "Confirm"}
                  </button>
                </div>
              </div>
            )}

            {isPanelOpen && activePanel.action === "repair" && (
              <div style={{ marginTop: 12, background: "#FFF7ED", border: "1px solid #FED7AA", borderRadius: 10, padding: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#9A3412", marginBottom: 6 }}>Select Part Coming Out for Repair</div>
                {installedInstances.length === 0 ? (
                  <>
                    <p style={{ fontSize: 12, color: "#6B7280", marginBottom: 8 }}>
                      No tracked part found on this machine — this part was likely installed before the Spare Parts system was set up.
                      Register it now with its serial number so it can be sent for repair.
                    </p>
                    <p style={{ fontSize: 11, color: "#92400E", fontWeight: 600, marginBottom: 8 }}>
                      Registering for machine: {siteMachines.find(m => m.id === request.machine_id)?.machine_name || "Unknown machine"}
                    </p>
                    <input placeholder="Serial No. — check physical label on the part *" value={form.legacy_serial_no}
                      onChange={e => setForm(p => ({ ...p, legacy_serial_no: e.target.value }))} style={{ ...inp, marginTop: 0 }} />
                    <p style={{ fontSize: 10, color: "#9CA3AF", marginTop: 2, marginBottom: 0 }}>
                      Not the part name — enter only the serial number printed/engraved on the physical part.
                    </p>
                    <input placeholder="Part No. (optional)" value={form.legacy_part_no}
                      onChange={e => setForm(p => ({ ...p, legacy_part_no: e.target.value }))} style={inp} />
                    <input placeholder="Brand (optional)" value={form.legacy_brand}
                      onChange={e => setForm(p => ({ ...p, legacy_brand: e.target.value }))} style={inp} />
                    <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                      <button onClick={closePanel} style={{ flex: 1, background: "#fff", border: "1px solid #E5E7EB", borderRadius: 8, padding: "8px 0", fontSize: 12, cursor: "pointer" }}>Cancel</button>
                      <button onClick={() => doRegisterLegacyAndRepair(request)} disabled={registeringLegacy || !form.legacy_serial_no.trim()} style={{ flex: 1, background: "#EA580C", color: "#fff", border: "none", borderRadius: 8, padding: "8px 0", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                        {registeringLegacy ? "..." : "Register & Send for Repair"}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <select value={form.partInstanceId} onChange={e => setForm(p => ({ ...p, partInstanceId: e.target.value }))} style={{ ...inp, marginTop: 0 }}>
                      <option value="">Select instance</option>
                      {installedInstances.map(inst => (
                        <option key={inst.id} value={inst.id}>{inst.part_instance_no} · S/N {inst.serial_no}</option>
                      ))}
                    </select>
                    <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                      <button onClick={closePanel} style={{ flex: 1, background: "#fff", border: "1px solid #E5E7EB", borderRadius: 8, padding: "8px 0", fontSize: 12, cursor: "pointer" }}>Cancel</button>
                      <button onClick={() => doResolveRepair(request)} disabled={submitting} style={{ flex: 1, background: "#EA580C", color: "#fff", border: "none", borderRadius: 8, padding: "8px 0", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                        {submitting ? "..." : "Confirm"}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            {isPanelOpen && activePanel.action === "complete_warranty" && (
              <div style={{ marginTop: 12, background: "#F5F3FF", border: "1px solid #DDD6FE", borderRadius: 10, padding: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#6A1B9A", marginBottom: 6 }}>Replacement Received from OEM</div>
                <input placeholder="Received Serial No. *" value={form.received_serial_no}
                  onChange={e => setForm(p => ({ ...p, received_serial_no: e.target.value }))} style={{ ...inp, marginTop: 0 }} />
                <select value={form.machine_id} onChange={e => setForm(p => ({ ...p, machine_id: e.target.value }))} style={inp}>
                  <option value="">Select machine to install into (optional)</option>
                  {siteMachines.map(m => (
                    <option key={m.id} value={m.id}>{m.machine_name || "Unnamed"} {m.model_no ? `· ${m.model_no}` : ""}</option>
                  ))}
                </select>
                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                  <button onClick={closePanel} style={{ flex: 1, background: "#fff", border: "1px solid #E5E7EB", borderRadius: 8, padding: "8px 0", fontSize: 12, cursor: "pointer" }}>Cancel</button>
                  <button onClick={() => doCompleteWarranty(request)} disabled={submitting} style={{ flex: 1, background: "#7C3AED", color: "#fff", border: "none", borderRadius: 8, padding: "8px 0", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                    {submitting ? "..." : "Confirm Received"}
                  </button>
                </div>
              </div>
            )}

            {isPanelOpen && activePanel.action === "complete_buy" && (
              <div style={{ marginTop: 12, background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 10, padding: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#0369A1", marginBottom: 6 }}>GRN Received</div>
                <input placeholder="Received Serial No. *" value={form.received_serial_no}
                  onChange={e => setForm(p => ({ ...p, received_serial_no: e.target.value }))} style={{ ...inp, marginTop: 0 }} />
                <select value={form.machine_id} onChange={e => setForm(p => ({ ...p, machine_id: e.target.value }))} style={inp}>
                  <option value="">Select machine to install into (optional)</option>
                  {siteMachines.map(m => (
                    <option key={m.id} value={m.id}>{m.machine_name || "Unnamed"} {m.model_no ? `· ${m.model_no}` : ""}</option>
                  ))}
                </select>
                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                  <button onClick={closePanel} style={{ flex: 1, background: "#fff", border: "1px solid #E5E7EB", borderRadius: 8, padding: "8px 0", fontSize: 12, cursor: "pointer" }}>Cancel</button>
                  <button onClick={() => doCompleteBuy(request)} disabled={submitting} style={{ flex: 1, background: "#0369A1", color: "#fff", border: "none", borderRadius: 8, padding: "8px 0", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                    {submitting ? "..." : "Confirm Received"}
                  </button>
                </div>
              </div>
            )}

            {isPanelOpen && activePanel.action === "complete_repair" && (
              <div style={{ marginTop: 12, background: "#FFF7ED", border: "1px solid #FED7AA", borderRadius: 10, padding: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#9A3412", marginBottom: 6 }}>Repaired Part Returned</div>
                <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#374151" }}>
                  <input type="checkbox" checked={form.reinstall} onChange={e => setForm(p => ({ ...p, reinstall: e.target.checked }))} />
                  Reinstall directly into a machine (uncheck to keep in store)
                </label>
                {form.reinstall && (
                  <select value={form.machine_id} onChange={e => setForm(p => ({ ...p, machine_id: e.target.value }))} style={inp}>
                    <option value="">Select machine *</option>
                    {siteMachines.map(m => (
                      <option key={m.id} value={m.id}>{m.machine_name || "Unnamed"} {m.model_no ? `· ${m.model_no}` : ""}</option>
                    ))}
                  </select>
                )}
                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                  <button onClick={closePanel} style={{ flex: 1, background: "#fff", border: "1px solid #E5E7EB", borderRadius: 8, padding: "8px 0", fontSize: 12, cursor: "pointer" }}>Cancel</button>
                  <button onClick={() => doCompleteRepair(request)} disabled={submitting} style={{ flex: 1, background: "#EA580C", color: "#fff", border: "none", borderRadius: 8, padding: "8px 0", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                    {submitting ? "..." : "Confirm"}
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
